import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Empty, Form, Input, InputNumber, Modal, Popconfirm, Result, Select, Skeleton, Switch, Table, Tag, Typography, message } from "antd";
import { useParams } from "react-router-dom";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";

const { Title, Text, Paragraph } = Typography;

// 主动式剧本:站长配置「行为信号 + 页面范围 → 主动气泡文案/推荐问题/场景说明注入」。
// 默认关闭;启用后 SDK 命中信号弹轻提示,访客点开成对话才计 answer。
export interface PlaybookDTO {
  id: string;
  name: string;
  enabled: boolean;
  signal: string;
  urlPattern: string;
  bubbleText: string;
  suggestions: string[];
  instructions: string;
  cooldownSeconds: number;
  maxPerSession: number;
  priority: number;
  triggerCount: number;
  openCount: number;
  updatedAt: number;
}

const SIGNAL_OPTIONS = [
  { value: "exit_intent", label: "离开意图（鼠标移向关闭页面）" },
  { value: "stuck", label: "疑似卡住（久留且反复滚动）" },
  { value: "rage_click", label: "连续狂点（元素不响应）" },
  { value: "form_abandon", label: "表单填一半放弃" },
  { value: "idle", label: "长时间无操作" },
  { value: "page_enter", label: "进入页面（延迟数秒）" },
];
const signalLabel = (value: string) => SIGNAL_OPTIONS.find((item) => item.value === value)?.label || value;

const SiteCopilotPlaybook: React.FC = () => {
  const { siteId = "" } = useParams<{ siteId: string }>();
  const { API_BASE_URL } = useGlobal();
  const [items, setItems] = useState<PlaybookDTO[]>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<PlaybookDTO | null | "new">(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const base = `${API_BASE_URL}/api/v3/sites/${encodeURIComponent(siteId)}/playbooks`;

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true); setError("");
    try {
      const response = await request.get(base, { signal });
      if (response.data?.code !== 200) throw new Error(response.data?.msg || "剧本读取失败");
      setItems(response.data.data?.items || []);
    } catch (reason: any) { if (reason?.code !== "ERR_CANCELED") setError(reason.response?.data?.msg || reason.message || "剧本读取失败"); }
    finally { if (!signal?.aborted) setLoading(false); }
  }, [base]);
  useEffect(() => { const controller = new AbortController(); void load(controller.signal); return () => controller.abort(); }, [load]);

  const openEditor = (item: PlaybookDTO | "new") => {
    setEditing(item);
    if (item === "new") {
      form.setFieldsValue({ name: "", enabled: false, signal: "exit_intent", urlPattern: "", bubbleText: "", suggestionsText: "", instructions: "", cooldownSeconds: 120, maxPerSession: 1, priority: 0 });
    } else {
      form.setFieldsValue({ ...item, suggestionsText: (item.suggestions || []).join("\n") });
    }
  };

  const save = async () => {
    const values = await form.validateFields();
    const payload = {
      name: values.name || "",
      enabled: Boolean(values.enabled),
      signal: values.signal,
      urlPattern: values.urlPattern || "",
      bubbleText: values.bubbleText,
      suggestions: String(values.suggestionsText || "").split("\n").map((item: string) => item.trim()).filter(Boolean).slice(0, 3),
      instructions: values.instructions || "",
      cooldownSeconds: Number(values.cooldownSeconds) || 120,
      maxPerSession: Number(values.maxPerSession) || 1,
      priority: Number(values.priority) || 0,
    };
    setSaving(true);
    try {
      const response = editing === "new"
        ? await request.post(base, payload)
        : await request.put(`${base}/${encodeURIComponent((editing as PlaybookDTO).id)}`, payload);
      if (response.data?.code !== 200) throw new Error(response.data?.msg || "保存失败");
      message.success("已保存");
      setEditing(null);
      void load();
    } catch (reason: any) { message.error(reason.response?.data?.msg || reason.message || "保存失败"); }
    finally { setSaving(false); }
  };

  const toggle = async (item: PlaybookDTO, enabled: boolean) => {
    try {
      const response = await request.put(`${base}/${encodeURIComponent(item.id)}`, { enabled });
      if (response.data?.code !== 200) throw new Error(response.data?.msg || "操作失败");
      setItems((current) => current?.map((row) => row.id === item.id ? { ...row, enabled } : row));
    } catch (reason: any) { message.error(reason.response?.data?.msg || reason.message || "操作失败"); }
  };

  const remove = async (item: PlaybookDTO) => {
    try {
      const response = await request.delete(`${base}/${encodeURIComponent(item.id)}`);
      if (response.data?.code !== 200) throw new Error(response.data?.msg || "删除失败");
      message.success("已删除");
      void load();
    } catch (reason: any) { message.error(reason.response?.data?.msg || reason.message || "删除失败"); }
  };

  if (loading && !items) return <main className="site-product-page"><Skeleton active paragraph={{ rows: 10 }} /></main>;
  if (error && !items) return <main className="site-product-page"><Result status="error" title="无法读取主动式剧本" subTitle={error} extra={<Button onClick={() => void load()}>重试</Button>} /></main>;

  const columns = [
    { title: "剧本", key: "name", render: (_: unknown, item: PlaybookDTO) => <span><strong>{item.name || "未命名剧本"}</strong><br /><Text type="secondary" style={{ fontSize: 12 }}>{item.bubbleText}</Text></span> },
    { title: "触发信号", dataIndex: "signal", render: (value: string) => <Tag>{signalLabel(value)}</Tag> },
    { title: "页面范围", dataIndex: "urlPattern", render: (value: string) => value ? <Text code>{value}</Text> : <Text type="secondary">全站</Text> },
    { title: "频控", key: "cap", render: (_: unknown, item: PlaybookDTO) => <Text type="secondary">{`每会话 ${item.maxPerSession} 次 / 冷却 ${item.cooldownSeconds}s`}</Text> },
    { title: "点开对话次数", key: "stats", render: (_: unknown, item: PlaybookDTO) => item.openCount },
    { title: "启用", key: "enabled", render: (_: unknown, item: PlaybookDTO) => <Switch checked={item.enabled} onChange={(checked) => void toggle(item, checked)} /> },
    {
      title: "操作", key: "actions", render: (_: unknown, item: PlaybookDTO) => <span style={{ display: "inline-flex", gap: 8 }}>
        <Button size="small" onClick={() => openEditor(item)}>编辑</Button>
        <Popconfirm title="删除该剧本？" description="删除后 SDK 端立即不再触发。" onConfirm={() => void remove(item)}><Button size="small" danger>删除</Button></Popconfirm>
      </span>,
    },
  ];

  return <main className="site-product-page" aria-labelledby="site-playbook-title">
    <header className="site-product-page__heading">
      <div>
        <Text type="secondary">主动式 AI</Text>
        <Title id="site-playbook-title" level={2}>主动剧本</Title>
        <Paragraph type="secondary">配置访客行为信号触发的主动提示：命中信号时挂件旁弹出一句话气泡，访客点开才进入对话（此时才计入回答用量）。默认全部关闭，启用后生效。</Paragraph>
      </div>
      <Button type="primary" icon={<i className="ri-add-line" />} onClick={() => openEditor("new")}>新增剧本</Button>
    </header>
    <Alert type="info" showIcon message="不打扰访客的三层兜底" description="除这里配置的每会话次数与冷却时间外，SDK 还内置全局频控（每次访问最多 2 次提示、两次间隔至少 30 秒），访客手动关闭气泡后本次访问不再提示。" style={{ marginBottom: 16 }} />
    <Table rowKey="id" columns={columns} dataSource={items} pagination={false} locale={{ emptyText: <Empty description="还没有主动剧本，点右上角「新增剧本」开始。" /> }} scroll={{ x: 960 }} />
    <Modal title={editing === "new" ? "新增剧本" : "编辑剧本"} open={editing !== null} onOk={() => void save()} onCancel={() => setEditing(null)} confirmLoading={saving} okText="保存" cancelText="取消" width={640} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="剧本名（仅自己可见）" rules={[{ max: 60 }]}><Input placeholder="如：定价页离开挽留" /></Form.Item>
        <Form.Item name="signal" label="触发信号" rules={[{ required: true }]}><Select options={SIGNAL_OPTIONS} /></Form.Item>
        <Form.Item name="urlPattern" label="页面范围（URL 包含该片段才触发，留空 = 全站）"><Input placeholder="如：/pricing" /></Form.Item>
        <Form.Item name="bubbleText" label="气泡文案（展示给访客的一句话）" rules={[{ required: true, message: "请填写气泡文案" }, { max: 100 }]}><Input.TextArea rows={2} maxLength={100} showCount placeholder="看起来你在对比套餐，需要我帮你算算哪个更划算吗？" /></Form.Item>
        <Form.Item name="suggestionsText" label="推荐问题（每行一条，最多 3 条；访客点开气泡后显示）"><Input.TextArea rows={3} placeholder={"帮我对比各套餐差异\n哪个套餐适合小型站点？"} /></Form.Item>
        <Form.Item name="instructions" label="场景说明（注入给 AI，访客不可见；如「重点引导升级 Pro，强调按年付优惠」）" rules={[{ max: 2000 }]}><Input.TextArea rows={3} maxLength={2000} showCount /></Form.Item>
        <div style={{ display: "flex", gap: 16 }}>
          <Form.Item name="cooldownSeconds" label="冷却（秒）" style={{ flex: 1 }}><InputNumber min={0} max={86400} style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="maxPerSession" label="每会话最多次数" style={{ flex: 1 }}><InputNumber min={1} max={10} style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="priority" label="优先级（大者先）" style={{ flex: 1 }}><InputNumber min={-100} max={100} style={{ width: "100%" }} /></Form.Item>
        </div>
        <Form.Item name="enabled" label="启用" valuePropName="checked"><Switch /></Form.Item>
      </Form>
    </Modal>
  </main>;
};

export default SiteCopilotPlaybook;
